import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/ui/button';
import { Card } from '@/ui/card';

const FOOTER_LINKS = ['Help', 'Privacy', 'Terms'];

type AuthLayoutProps = {
  title?: string;
  description?: string;
  children: ReactNode;
};

export function AuthCard({ className, ...props }: ComponentProps<typeof Card>) {
  return (
    <Card
      className={cn(
        'p-6 shadow-card ring-0 max-md:rounded-none max-md:bg-transparent max-md:p-0 max-md:shadow-none',
        className
      )}
      {...props}
    />
  );
}

export function AuthLayout({ title, description, children }: AuthLayoutProps) {
  return (
    <div className="flex min-h-svh bg-background text-foreground md:bg-muted">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16.25 shrink-0 items-center px-4 md:px-8 lg:px-15">
          <img
            src="/brand/tixbase-logo.svg"
            alt="Tixbase"
            width={118}
            height={24}
            className="h-6 w-auto dark:hidden"
          />

          <img
            src="/brand/tixbase-logo-on-dark.svg"
            alt="Tixbase"
            width={118}
            height={24}
            className="hidden h-6 w-auto dark:block"
          />
        </header>

        <main className="flex flex-1 items-start justify-center px-4 pt-6 pb-4 md:items-center md:py-4">
          <div className="flex w-full max-w-112.5 flex-col gap-4">
            {title && (
              <div className="flex flex-col gap-0.5">
                <h1 className="text-h3-mobile text-balance md:text-tablet-h5 lg:text-h3">{title}</h1>

                {description && (
                  <p className="text-mobile-body2 text-pretty md:text-tablet-body2 lg:text-body4">
                    {description}
                  </p>
                )}
              </div>
            )}
            {children}
          </div>
        </main>

        <footer className="flex min-h-16.25 shrink-0 flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2 md:px-8 lg:px-15">
          {FOOTER_LINKS.map((label) => (
            <Button key={label} type="button" variant="text" size="pill-sm">
              {label}
            </Button>
          ))}
        </footer>
      </div>

      <div className="sticky top-0 hidden h-svh w-1/2 shrink-0 py-4 pr-4 lg:block">
        <img
          src="/auth/sign-in-illustration.png"
          alt=""
          width={623}
          height={768}
          className="size-full rounded-3xl object-cover object-left"
        />
      </div>
    </div>
  );
}
